import type { DuelLevel } from '../battle/aiSkill';
import { attackInterval } from '../characters/stats';
import type { CharacterDef } from '../characters/types';
import {
  arenaCombatSignature,
  calibrationTable,
  levelSignatureStands,
  type ArenaCalibration,
} from './arenaCalibration';
import { arenaForGame } from './definition';
import { createRng } from '../rng/seededRandom';
import { curseLogOdds } from './curse';
import { cellBrightness, factionAffinity, luminosityBonus } from './luminosity';
import { pieceById } from './movement';
import type { BattleResult, GameContext, GameState, Piece } from './types';

export interface Combatant {
  piece: Piece;
  hitPoints: number;
  damage: number;
}

/**
 * The hit points the square's light adds to a piece in a fight there: what the duel's intro names,
 * and the part of `effectiveHitPoints` the board's cycle decides.
 */
export function luminosityHitPoints(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  x = piece.x,
  y = piece.y,
): number {
  const character = ctx.roster[piece.character];
  // A summoned elemental is a creature of its element, not of light or dark: the cycle neither
  // helps nor hurts it, so its strength is the same whoever calls it and wherever it fights. The
  // balance lab takes the cycle out of every fight it measures (`GameContext.luminosity`).
  if (!character || character.summoned || ctx.luminosity === 'off') return 0;
  const bonus = luminosityBonus(
    factionAffinity(ctx, piece.faction),
    cellBrightness(ctx, state, x, y),
  );
  if (!character.spellcaster) return bonus;
  // Each spell cast weakens the mage's magical protection a little (as in the original).
  const faction = state.factions.find((f) => f.id === piece.faction);
  return Math.max(0, bonus - (faction?.spellsCast ?? 0));
}

/** Hit points a piece brings into a fight on its current square: base + luminosity bonus − wounds. */
export function effectiveHitPoints(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  x = piece.x,
  y = piece.y,
): number {
  const character = ctx.roster[piece.character];
  if (!character) return 1;
  return Math.max(
    1,
    character.hitPoints + luminosityHitPoints(ctx, state, piece, x, y) - piece.damage,
  );
}

/**
 * Whether `piece` fights `opponent` as a copy of it (D-56): a shapeshifter becomes its opponent,
 * body and weapon, unless the opponent is a shapeshifter too.
 */
export function copiesOpponent(ctx: GameContext, piece: Piece, opponent: Piece): boolean {
  return (
    ctx.roster[piece.character]?.attack.type === 'mimic' &&
    ctx.roster[opponent.character]?.attack.type !== 'mimic'
  );
}

/** The character whose body and weapon `piece` fights `opponent` with (D-56). */
export function duelForm(ctx: GameContext, piece: Piece, opponent: Piece): string {
  return copiesOpponent(ctx, piece, opponent) ? opponent.character : piece.character;
}

/**
 * The hit points a shapeshifter's copy fights with (D-56): the opponent's body at the
 * shapeshifter's own share of health, and what the square gives the shapeshifter's side. A fresh
 * shapeshifter makes a fresh copy and a wounded one a wounded copy, whatever the opponent's own
 * wounds, and the square's light still favours the side it favours.
 */
export function copyHitPoints(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  opponent: Piece,
  x = piece.x,
  y = piece.y,
): number {
  const own = ctx.roster[piece.character];
  const body = ctx.roster[opponent.character];
  if (!own || !body) return effectiveHitPoints(ctx, state, piece, x, y);
  const health = Math.max(0, own.hitPoints - piece.damage) / own.hitPoints;
  return Math.max(1, body.hitPoints * health + luminosityHitPoints(ctx, state, piece, x, y));
}

/** The hit points `piece` fights `opponent` with: its own, or a shapeshifter's copy (D-56). */
export function duelHitPoints(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  opponent: Piece,
  x = piece.x,
  y = piece.y,
): number {
  return copiesOpponent(ctx, piece, opponent)
    ? copyHitPoints(ctx, state, piece, opponent, x, y)
    : effectiveHitPoints(ctx, state, piece, x, y);
}

/** One side of a fight as the odds weigh it; against `opponent`, a shapeshifter is its copy. */
export function combatant(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  x: number,
  y: number,
  opponent?: Piece,
): Combatant {
  const form = opponent ? duelForm(ctx, piece, opponent) : piece.character;
  return {
    piece,
    hitPoints: opponent
      ? duelHitPoints(ctx, state, piece, opponent, x, y)
      : effectiveHitPoints(ctx, state, piece, x, y),
    damage: ctx.roster[form]?.attack.damage ?? 1,
  };
}

/**
 * What one side is worth in a duel, as the arena settles it — measured, not reasoned.
 * `pnpm balance:duels` fights all 400 ordered pairs of the roster in the real arena with the real
 * AI on both sides, and these are the terms that survived holding whole characters out of the fit:
 *
 * - **Damage per second, not damage.** A dragon's bolt hits for nine and takes 1.55 s to gather;
 *   an archer's arrow hits for five every 0.85 s. Counting only the blow made hitting harder
 *   strictly better, which is not what the arena does.
 * - **A burst costs the one who lets it off** five percent of its own hit points and must be used
 *   at close range, so its stated damage still overstates what it lands.
 * - **A contact weapon has to reach its enemy**, across ground the other side is shooting over.
 *
 * A fighter's speed and size, which the tier sets, were tried and dropped: they bought nothing
 * once the three above were in.
 */
function duelStrength(ctx: GameContext, me: Combatant, foe: Combatant): number {
  // A shapeshifter fights as its opponent, body and weapon (D-56); two of them fight hand to hand,
  // as the arena has them.
  const character = ctx.roster[duelForm(ctx, me.piece, foe.piece)];
  const foeCharacter = ctx.roster[duelForm(ctx, foe.piece, me.piece)];
  const own = character?.attack ?? { type: 'melee' as const, damage: me.damage };
  const attack = own.type === 'mimic' ? { ...own, type: 'melee' as const } : own;
  const type = attack.type;
  let output = attack.damage / attackInterval(attack);
  if (type === 'burst') output *= BURST_SELF_COST;
  if (type === 'burn') output *= BURN_DUTY;
  const foeType =
    foeCharacter?.attack.type === 'mimic' ? 'melee' : (foeCharacter?.attack.type ?? 'melee');
  if (type === 'melee' && foeType !== 'melee') output *= MELEE_APPROACH;
  const damageType = attack.damageType ?? 'physical';
  const effect = attack.effectChance ?? 1;
  const protection = foeCharacter?.combat?.protection ?? 0;
  const resistance = foeCharacter?.combat?.resistances?.[damageType] ?? 0;
  const speed = character?.combat?.speed ?? 1;
  output *= effect * (1 - protection) * (1 - resistance) * Math.sqrt(speed);
  return me.hitPoints * output;
}

/** A burst costs five percent of the caster's life and still needs to get close before releasing. */
const BURST_SELF_COST = 0.8;
/** A contact weapon has to cross the ground the other side is shooting over. */
const MELEE_APPROACH = 0.5;
/**
 * A burning ring is up half the time at best: it spends its account as fast as it fills it. What
 * it buys for that is a bite that cannot miss and needs no aiming, so unlike everything else its
 * stated rate is what it actually lands. The balance pass measures whether the two cancel.
 */
const BURN_DUTY = 1.5;
/**
 * How sharply an advantage in strength turns into a win. Above one because a duel is won by
 * killing first, not by out-scoring: the fighter with half again the strength is not winning three
 * fights in five, it is winning three in four. Fitted to the measured duels by likelihood, which
 * is what an odds number has to be scored by — squared error rewards answering "even fight" to
 * everything, and that is exactly what the rule this replaced was doing.
 */
const DECISIVENESS = 1.5;
/**
 * How much a wounded attacker's odds fall beyond what its share of the strength predicts, for a
 * table without measured wounded odds. Measured on 5,570 real Arena fights recorded during the
 * Expert team stages of 2026-09-21: attackers with half or more of their health gone won 55 % of
 * their fights where the strength ratio alone said 74 %, while defender wounds and luminosity
 * were predicted within a point or two. It is a single shift of the log-odds, which cannot express
 * what the 9,054 fights of 2026-09-22 showed: a piece the neutral table calls certain (Banshee over
 * Knight, 100 % of 188 whole fights) wins 60 % once it has lost a third of its health. So the
 * originals carry measured wounded odds (`ArenaCalibration.wounded`) and this is the fallback.
 */
const ATTACKER_WOUNDS = 1;
/**
 * What the square's light is worth, per unit of the log of the share of health it lifts a whole
 * fighter to. The duel gives the bonus as a larger body, which the strength share (`DECISIVENESS`)
 * under-reads: on 56 Light-against-Dark pairs in the Arena at +7 hit points (2026-09-27), the
 * bonus moved the log-odds 1.8 times as far as the share said. Those fights did not separate the
 * role from the side (the bonus on a Dark defender weighed 2.4 times, on a Light attacker 1.3), so
 * one weight serves both roles and keeps the odds reciprocal. On the board the under-read had the
 * side the square favours win a point or two more often than its odds.
 */
export const SQUARE_LIGHT = 2.7;

/** A share of health on the log-odds: light above whole (`SQUARE_LIGHT`), wounds below it. */
export function healthLogOdds(share: number): number {
  return (share >= 1 ? SQUARE_LIGHT : DECISIVENESS) * Math.log(share);
}

const logit = (p: number): number => {
  const q = Math.min(1 - 1e-6, Math.max(1e-6, p));
  return Math.log(q / (1 - q));
};

/**
 * The attacker's log-odds at `share` of its health against a whole defender: the measured wounded
 * odds at the table's shares and the neutral odds at 1, read on the line between the nearest two;
 * below the lowest share, along the lowest segment.
 */
function woundedLogit(
  wounded: NonNullable<ArenaCalibration['wounded']>,
  odds: readonly number[],
  neutral: number,
  share: number,
): number {
  const knots = [...wounded.shares.map((s, i) => [s, logit(odds[i]!)] as const), [1, neutral]];
  let i = 0;
  while (i < knots.length - 2 && share > knots[i + 1]![0]) i++;
  const [s0, l0] = knots[i]!;
  const [s1, l1] = knots[i + 1]!;
  return l0 + ((l1 - l0) * (share - s0)) / (s1 - s0);
}

/**
 * The level a fight's odds are read at: in Roll and Card, the one both seats fight at when they
 * share it (`GameContext.cardLevels`, a person's seat Expert); anything else, and the Arena always,
 * Expert, the odds the board has read since the tables were first measured.
 */
export function fightLevel(
  ctx: GameContext,
  attacker: Piece,
  defender: Piece,
  levels: GameContext['cardLevels'] = ctx.cardLevels,
): DuelLevel {
  if (ctx.combatMode !== 'roll' && ctx.combatMode !== 'card') return 'expert';
  const a = levels?.[attacker.faction] ?? 'expert';
  return a === (levels?.[defender.faction] ?? 'expert') ? a : 'expert';
}

/**
 * Probability that the attacker wins the pending battle, from the two sides' strength: measured
 * where the game has a table for the fight (`fightLevel`), else reasoned from the characters.
 */
export function attackerWinProbability(
  ctx: GameContext,
  state: GameState,
  levels?: GameContext['cardLevels'],
): number {
  const battle = state.pendingBattle;
  if (!battle) return 0;
  const attacker = pieceById(state, battle.attackerId);
  const defender = pieceById(state, battle.defenderId);
  if (!attacker || !defender) return 0;
  const a = combatant(ctx, state, attacker, battle.x, battle.y, defender);
  const d = combatant(ctx, state, defender, battle.x, battle.y, attacker);
  const calibrated = calibratedProbability(
    ctx,
    state,
    a,
    d,
    fightLevel(ctx, attacker, defender, levels),
  );
  let p: number;
  if (calibrated !== undefined) p = calibrated;
  else {
    const strengthA = duelStrength(ctx, a, d) ** DECISIVENESS;
    const strengthD = duelStrength(ctx, d, a) ** DECISIVENESS;
    p = strengthA / (strengthA + strengthD);
  }
  // Curse costs the same in every combat mode (D-31); a fight without one keeps its odds exactly.
  const curse = curseLogOdds(attacker, defender);
  return curse === 0 ? p : 1 / (1 + Math.exp(-(logit(p) + curse)));
}

/**
 * The table a fight reads: the level's own when both sides fight at one level other than Expert and
 * it stands for both characters (Roll and Card, phase D), else Expert's; and Expert's beside it,
 * whose measured wounded odds shape a level's wounds.
 */
function fightTables(
  ctx: GameContext,
  state: GameState,
  ac: CharacterDef,
  dc: CharacterDef,
  level: DuelLevel,
): { table: ArenaCalibration; expert?: ArenaCalibration } | undefined {
  const definition = ctx.definition!;
  const { arena, tileset } = arenaForGame(definition, state);
  const signature = arenaCombatSignature(arena, tileset);
  const stands = (table: ArenaCalibration | undefined, at: DuelLevel) =>
    table &&
    levelSignatureStands(ac, table.characters[ac.id], at) &&
    levelSignatureStands(dc, table.characters[dc.id], at)
      ? table
      : undefined;
  const expert = stands(calibrationTable(definition, signature), 'expert');
  const own =
    level !== 'expert' ? stands(calibrationTable(definition, signature, level), level) : undefined;
  const table = own ?? expert;
  return table && { table, expert };
}

function calibratedProbability(
  ctx: GameContext,
  state: GameState,
  a: Combatant,
  d: Combatant,
  level: DuelLevel,
): number | undefined {
  const definition = ctx.definition;
  if (!definition?.arenaCalibration?.length) return undefined;
  const ac = ctx.roster[a.piece.character]!,
    dc = ctx.roster[d.piece.character]!;
  const tables = fightTables(ctx, state, ac, dc, level);
  if (!tables) return undefined;
  const { table } = tables;
  const ids = [ac.id, dc.id].sort();
  /** The attacker's neutral odds in a table, or undefined where it has none for the pair. */
  const neutral = (t: ArenaCalibration) => {
    const measured = ac.id === dc.id ? 0.5 : t.probabilities[ids.join('|')];
    return measured === undefined ? undefined : ac.id === ids[0] ? measured : 1 - measured;
  };
  const p = neutral(table);
  if (p === undefined) return undefined;
  // A level's table holds its neutral odds, and its wounds take the shape Expert's measured wounded
  // odds give them: the same shift of the log-odds from the pair's neutral odds (phase D). A table
  // with wounded odds of its own shapes itself, exactly as before.
  const shape = table.wounded ? table : tables.expert?.wounded ? tables.expert : undefined;
  const pShape = shape && shape !== table ? neutral(shape) : p;
  const shift = shape && shape !== table && pShape !== undefined ? logit(p) - logit(pShape) : 0;
  const woundedOdds = pShape !== undefined ? shape?.wounded : undefined;
  // A shapeshifter's share is of the body it copies (D-56), as the arena gives it.
  const aBody = ctx.roster[duelForm(ctx, a.piece, d.piece)]!.hitPoints;
  const dBody = ctx.roster[duelForm(ctx, d.piece, a.piece)]!.hitPoints;
  const aShare = a.hitPoints / aBody;
  const dShare = d.hitPoints / dBody;
  const wounded = woundedOdds?.probabilities[`${ac.id}>${dc.id}`];
  const defended = woundedOdds?.defender?.[`${ac.id}>${dc.id}`];
  // The defender: the square's light lifts a whole one above its share (`SQUARE_LIGHT`); a
  // wounded one reads its measured odds where the table has them, as an attacker's are read, for
  // a share of health is worth more to some pieces than others and the strength share alone was
  // ten points short against wounded defenders on the board (gate of 2026-09-27).
  const defender =
    dShare >= 1
      ? -healthLogOdds(dShare)
      : defended && woundedOdds
        ? woundedLogit(woundedOdds, defended, logit(pShape!), dShare) - logit(pShape!)
        : -DECISIVENESS * Math.log(dShare);
  if (wounded && woundedOdds && aShare < 1)
    // The attacker's own wounds are measured.
    return (
      1 /
      (1 +
        Math.exp(-(woundedLogit(woundedOdds, wounded, logit(pShape!), aShare) + shift + defender)))
    );
  // A whole attacker gains its light (`SQUARE_LIGHT`); without measured wounded odds a wounded
  // one keeps its strength share and loses once more for the share of its own health it has left
  // (`ATTACKER_WOUNDS`).
  const x =
    logit(p) +
    healthLogOdds(aShare) +
    defender +
    (woundedOdds
      ? 0
      : ATTACKER_WOUNDS *
        Math.log(a.hitPoints / (a.hitPoints + (a.piece.damage * aBody) / ac.hitPoints)));
  return 1 / (1 + Math.exp(-x));
}

/**
 * Milestone-1 stand-in for the arena: settles the pending battle with a seeded roll.
 * The arena (M2) produces the same `BattleResult`, so the rest of the rules do not change.
 */
export function autoResolveBattle(
  ctx: GameContext,
  state: GameState,
): { result: BattleResult; rng: number } {
  const battle = state.pendingBattle;
  if (!battle) throw new Error('No battle to resolve');
  const rng = createRng(state.rng);
  const p = attackerWinProbability(ctx, state);
  const roll = rng.next();
  const doubleKill = rng.next() < 0.04;
  const attacker = pieceById(state, battle.attackerId)!;
  const defender = pieceById(state, battle.defenderId)!;
  const attackerWins = roll < p;
  const winner = attackerWins ? attacker : defender;
  const loser = attackerWins ? defender : attacker;
  const winnerHp = effectiveHitPoints(ctx, state, winner, battle.x, battle.y);
  const loserStrength =
    effectiveHitPoints(ctx, state, loser, battle.x, battle.y) *
    (ctx.roster[loser.character]?.attack.damage ?? 1);
  const winnerStrength = winnerHp * (ctx.roster[winner.character]?.attack.damage ?? 1);
  // Closer fights leave deeper wounds; the winner always keeps at least one hit point.
  const closeness = Math.min(1, loserStrength / winnerStrength);
  const wounds = Math.min(
    winnerHp - 1,
    Math.round(winnerHp * closeness * (0.3 + 0.5 * rng.next())),
  );
  return {
    result: doubleKill
      ? { winnerId: null, winnerDamage: 0 }
      : { winnerId: winner.id, winnerDamage: winner.damage + wounds },
    rng: rng.state(),
  };
}
